import { MIN_PRICE_CENTS } from "./money";

/** Variant vocabulary shared by the store, the admin panel and the server. */

export const CONDITIONS = ["NM", "LP", "MP", "HP", "DMG", "SEALED"] as const;
export type Condition = (typeof CONDITIONS)[number];

export const FINISHES = ["normal", "holo", "reverse", "first_edition"] as const;
export type Finish = (typeof FINISHES)[number];

export const VARIANT_LANGUAGES = ["en", "es", "ja", "pt", "fr", "de", "it", "ko", "zh"] as const;

export const CHANNELS = ["web", "local", "event", "mercadolibre", "other"] as const;
export type Channel = (typeof CHANNELS)[number];

/** Manual stock operations from the admin panel. */
export const MANUAL_MOVEMENTS = ["purchase", "sale", "adjustment", "count", "return"] as const;
export type ManualMovement = (typeof MANUAL_MOVEMENTS)[number];

/** Share of the NM reference price a condition sells for (the admin can lock any price). */
export const CONDITION_MULTIPLIER: Record<Condition, number> = {
  SEALED: 1,
  NM: 1,
  LP: 0.85,
  MP: 0.7,
  HP: 0.5,
  DMG: 0.3,
};

export const CONDITION_LABEL: Record<Condition, { es: string; en: string }> = {
  NM: { es: "Near Mint", en: "Near Mint" },
  LP: { es: "Poco jugada (LP)", en: "Lightly Played" },
  MP: { es: "Jugada (MP)", en: "Moderately Played" },
  HP: { es: "Muy jugada (HP)", en: "Heavily Played" },
  DMG: { es: "Dañada", en: "Damaged" },
  SEALED: { es: "Sellado", en: "Sealed" },
};

export const FINISH_LABEL: Record<Finish, { es: string; en: string }> = {
  normal: { es: "Normal", en: "Normal" },
  holo: { es: "Holo", en: "Holo" },
  reverse: { es: "Reverse holo", en: "Reverse holo" },
  first_edition: { es: "1ª edición", en: "1st Edition" },
};

export const LANGUAGE_LABEL: Record<string, { es: string; en: string }> = {
  en: { es: "Inglés", en: "English" },
  es: { es: "Español", en: "Spanish" },
  ja: { es: "Japonés", en: "Japanese" },
  pt: { es: "Portugués", en: "Portuguese" },
  fr: { es: "Francés", en: "French" },
  de: { es: "Alemán", en: "German" },
  it: { es: "Italiano", en: "Italian" },
  ko: { es: "Coreano", en: "Korean" },
  zh: { es: "Chino", en: "Chinese" },
};

export interface VariantKey {
  condition: Condition;
  finish: Finish;
  language: string;
}

export function isCondition(v: unknown): v is Condition {
  return typeof v === "string" && (CONDITIONS as readonly string[]).includes(v);
}
export function isFinish(v: unknown): v is Finish {
  return typeof v === "string" && (FINISHES as readonly string[]).includes(v);
}
export function isChannel(v: unknown): v is Channel {
  return typeof v === "string" && (CHANNELS as readonly string[]).includes(v);
}
export function isVariantLanguage(v: unknown): v is string {
  return typeof v === "string" && /^[a-z]{2}$/.test(v);
}

/** Only single cards have a condition (NM…DMG) and a finish (holo, reverse…). */
export const CARD_CATEGORY = "singles";

export function isCardCategory(category: string): boolean {
  return category === CARD_CATEGORY;
}

/** Conditions and finishes that make sense for a product category. */
export function variantOptionsFor(category: string): { conditions: readonly Condition[]; finishes: readonly Finish[] } {
  return isCardCategory(category)
    ? { conditions: CONDITIONS.filter((c) => c !== "SEALED"), finishes: FINISHES }
    : { conditions: ["SEALED"], finishes: ["normal"] };
}

/** The default variant key for a category (sealed products: SEALED + normal). */
export function defaultVariantFor(category: string, prefs: { condition: Condition; finish: Finish }): { condition: Condition; finish: Finish } {
  const { conditions, finishes } = variantOptionsFor(category);
  return {
    condition: conditions.includes(prefs.condition) ? prefs.condition : conditions[0],
    finish: finishes.includes(prefs.finish) ? prefs.finish : finishes[0],
  };
}

/** Null when condition/finish fit the category, else a short reason. */
export function variantCategoryError(category: string, v: { condition: Condition; finish: Finish }): string | null {
  const { conditions, finishes } = variantOptionsFor(category);
  if (!conditions.includes(v.condition)) return isCardCategory(category) ? "cards cannot be SEALED" : "sealed products are always SEALED";
  if (!finishes.includes(v.finish)) return "only cards have a finish";
  return null;
}

/** "SSP-238-NM-EN", "SSP-238-LP-H-ES" (H = holo, R = reverse, 1E = first edition). */
export function variantSku(productSku: string, v: VariantKey): string {
  const finish = { normal: "", holo: "-H", reverse: "-R", first_edition: "-1E" }[v.finish];
  return `${productSku}-${v.condition}${finish}-${v.language.toUpperCase()}`;
}

/** Suggested price (USD cents) for a condition: reference NM price × multiplier, up to 5 cents, never below the floor. */
export function suggestedPrice(refPrice: number, condition: Condition, minPrice = MIN_PRICE_CENTS): number {
  return Math.max(minPrice, Math.ceil((refPrice * CONDITION_MULTIPLIER[condition]) / 5 - 1e-9) * 5);
}

/** "Near Mint · Holo · Inglés" (normal finish omitted). */
export function variantLabel(v: VariantKey, locale: "es" | "en"): string {
  const parts = [CONDITION_LABEL[v.condition][locale]];
  if (v.finish !== "normal") parts.push(FINISH_LABEL[v.finish][locale]);
  parts.push(LANGUAGE_LABEL[v.language]?.[locale] ?? v.language.toUpperCase());
  return parts.join(" · ");
}
